import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { ApiError, isMfaChallenge, type LoginResponse } from '../auth/api';
import { AuthCard, Button, FieldLabel, FormError, TextInput } from '../components/FormPrimitives';

interface LocationState {
  from?: { pathname?: string; search?: string };
}

/** S-2: what the account has enrolled. Defaults to the S-1 shape
 *  (authenticator only) so an API response without mfa_methods behaves
 *  exactly as it did before this feature. */
const DEFAULT_MFA_METHODS = { totp: true, sms: false };

export default function Login() {
  const { user, signIn, completeMfaSignIn, sendMfaSms } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Pathname plus query string, so a link like the AI app approval screen
  // (/oauth/authorize?client_id=...) still works after signing in.
  const fromState = (location.state as LocationState | null)?.from;
  const from = fromState?.pathname ? fromState.pathname + (fromState.search ?? '') : '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Default true — most logins are from the agent's own phone/laptop. Uncheck
  // for shared/public devices to get a 1-day session instead of 14.
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /*
   * S-1 (Daniel 2026-08-04): the two-factor step.
   *
   * When the password checks out for an MFA-enrolled account, the API
   * returns a short-lived challenge token instead of a session. The form
   * swaps to a code entry; the token lives only in this component's state
   * (never tokenStore; it is not a session and requireAuth rejects it).
   *
   * REPO RULE: every hook sits above the conditional returns below
   * (React error #310), including all of these, even though they are only
   * meaningful during the code step.
   */
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  // Toggles the input between 6-digit authenticator mode and free-text
  // recovery-code mode. Same endpoint either way; the server tries both.
  const [useRecovery, setUseRecovery] = useState(false);
  // Set when the login succeeded via a recovery code: we pause on an
  // interstitial to tell them how many codes remain, because navigating
  // instantly would eat the one chance to say "you're running low".
  const [recoveryNotice, setRecoveryNotice] = useState<{
    remaining: number;
    result: LoginResponse;
  } | null>(null);

  /*
   * S-2 (Daniel 2026-08-04): the texted-code path.
   *
   * mfaMethods comes straight off the challenge and decides what this step
   * offers: an "authenticator code" label and nothing else for a TOTP-only
   * account (unchanged from S-1), a "Text me a code" button when SMS is
   * enrolled, and for an SMS-ONLY account an automatic send so the person
   * is not staring at an empty box wondering what is supposed to fill it.
   *
   * smsSentTo holds the masked destination the API echoes back, which is
   * what makes the confirmation line trustworthy: it is the number the
   * server actually texted, not one the browser assumed.
   */
  const [mfaMethods, setMfaMethods] = useState(DEFAULT_MFA_METHODS);
  const [smsSentTo, setSmsSentTo] = useState<string | null>(null);
  const [sendingSms, setSendingSms] = useState(false);

  /*
   * Auto-send guard. Holds the challenge token a code was already requested
   * for, so no re-render (and no StrictMode double-invoke of the effect
   * below) can fire a second text. A ref rather than state deliberately:
   * writing state here would re-render, re-run the effect, and be the very
   * loop the guard exists to prevent. Each fresh login mints a new token, so
   * a genuine second attempt is not blocked.
   */
  const autoSentForToken = useRef<string | null>(null);

  const requestSmsCode = useCallback(
    async (token: string) => {
      setSendingSms(true);
      setError(null);
      try {
        const res = await sendMfaSms(token);
        setSmsSentTo(res.sent_to_masked);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // The 5-minute challenge died. Same recovery as a failed verify:
          // back to the password step rather than a box that can never work.
          setMfaToken(null);
          setCode('');
          setError('That sign-in attempt expired. Enter your password again.');
        } else {
          // 429 ("Too many code requests…") and 502 (provider down) both
          // arrive with a usable sentence from the API; show it inline.
          setError(
            err instanceof ApiError
              ? err.message
              : 'Could not send the text message. Try again in a moment.',
          );
        }
      } finally {
        setSendingSms(false);
      }
    },
    [sendMfaSms],
  );

  // REPO RULE: this hook, like every other one, sits above the conditional
  // returns below (React error #310).
  useEffect(() => {
    if (!mfaToken) return;
    // Only when the text IS the second factor. A user who also has an
    // authenticator gets the button instead: spending one of their three
    // sends, and a Quo message, on somebody who was going to open 1Password
    // anyway is the wrong default.
    if (!mfaMethods.sms || mfaMethods.totp) return;
    if (autoSentForToken.current === mfaToken) return;
    autoSentForToken.current = mfaToken;
    void requestSmsCode(mfaToken);
  }, [mfaToken, mfaMethods, requestSmsCode]);

  // If already authenticated, bounce away from /login
  if (user) {
    return <Navigate to={user.must_change_password ? '/change-password' : from} replace />;
  }

  /** True when the texted code is the ONLY factor, which changes the labels
   *  from "authenticator" wording to "text message" wording. */
  const smsOnly = mfaMethods.sms && !mfaMethods.totp;

  function finishLogin(result: LoginResponse) {
    navigate(result.user.must_change_password ? '/change-password' : from, { replace: true });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(email.trim().toLowerCase(), password, remember);
      // S-1: enrolled account. Hold the challenge token and swap the form
      // to the code step instead of navigating.
      if (isMfaChallenge(result)) {
        setMfaToken(result.mfa_token);
        setCode('');
        setUseRecovery(false);
        // S-2: mfa_methods rides in on the same object as the token. Absent
        // means an API that predates this feature, which was TOTP-only.
        setMfaMethods(result.mfa_methods ?? DEFAULT_MFA_METHODS);
        setSmsSentTo(null);
        return;
      }
      finishLogin(result);
    } catch (err) {
      setError(humanizeSignInError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await completeMfaSignIn(mfaToken, code.trim());
      if (typeof result.recovery_codes_remaining === 'number') {
        // Signed in with a recovery code. Pause to show how many are left.
        setRecoveryNotice({ remaining: result.recovery_codes_remaining, result });
        return;
      }
      finishLogin(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // The 5-minute challenge token expired (or MFA state changed).
        // Send them back to the password step rather than a dead end.
        setMfaToken(null);
        setCode('');
        setSmsSentTo(null);
        setError('That sign-in attempt expired. Enter your password again.');
      } else {
        // 400 bad code and 429 rate limit both carry a clear message from
        // the API; show it inline above the input.
        setError(err instanceof ApiError ? err.message : 'Verification failed. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── S-1 interstitial: recovery code used, N codes left ───────────────
  if (recoveryNotice) {
    return (
      <AuthCard title="Transaction Rocket" subtitle="Signed in with a recovery code.">
        <p style={{ fontSize: 14, color: 'var(--text-body)', margin: '0 0 8px' }}>
          That recovery code is now used up.{' '}
          <strong>
            {recoveryNotice.remaining === 0
              ? 'You have no recovery codes left.'
              : `You have ${recoveryNotice.remaining} recovery code${recoveryNotice.remaining === 1 ? '' : 's'} left.`}
          </strong>
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 20px' }}>
          {recoveryNotice.remaining <= 2
            ? 'Turn two-factor off and back on from the Team page to get a fresh set of codes.'
            : 'You can manage two-factor from the Team page.'}
        </p>
        <Button onClick={() => finishLogin(recoveryNotice.result)} style={{ width: '100%' }}>
          Continue
        </Button>
      </AuthCard>
    );
  }

  // ── S-1 code step, S-2 added the text-message controls ────────────────
  if (mfaToken) {
    return (
      <AuthCard title="Transaction Rocket" subtitle="Enter your two-factor code.">
        <form onSubmit={onSubmitCode} noValidate>
          <FormError message={error} />

          <div style={{ marginBottom: 16 }}>
            <FieldLabel htmlFor="mfa-code">
              {useRecovery ? 'Recovery code' : smsOnly ? 'Texted code' : 'Authenticator code'}
            </FieldLabel>
            {useRecovery ? (
              <TextInput
                id="mfa-code"
                name="mfa-code"
                type="text"
                autoComplete="off"
                autoFocus
                placeholder="XXXX-XXXX-XX"
                required
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                disabled={submitting}
              />
            ) : (
              <TextInput
                id="mfa-code"
                name="mfa-code"
                type="text"
                // inputMode numeric brings up the digit keypad on phones;
                // one-time-code lets iOS/Android autofill a code they saw.
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                required
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                disabled={submitting}
                style={{ letterSpacing: '0.2em', fontSize: 18, textAlign: 'center' }}
              />
            )}
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
              {useRecovery
                ? 'One of the codes you saved when you turned two-factor on. Each works once.'
                : smsSentTo
                  ? `Code sent to ${smsSentTo}. It expires in 5 minutes.`
                  : smsOnly
                    ? sendingSms
                      ? 'Texting you a code…'
                      : 'The 6-digit code we text to your cell.'
                    : 'The 6-digit code from your authenticator app.'}
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting || !code.trim()}
            style={{ width: '100%', marginBottom: 12 }}
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </Button>

          {/* S-2: the text-message escape hatch. Hidden in recovery-code mode,
              where a texted code is not what the box is waiting for. */}
          {mfaMethods.sms && !useRecovery && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void requestSmsCode(mfaToken)}
              disabled={submitting || sendingSms}
              style={{ width: '100%', marginBottom: 12 }}
            >
              {sendingSms ? 'Sending…' : smsSentTo ? 'Text me a new code' : 'Text me a code'}
            </Button>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => {
                setUseRecovery((v) => !v);
                setCode('');
                setError(null);
              }}
              disabled={submitting}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--brand-primary)',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {useRecovery
                ? smsOnly
                  ? 'Use a texted code'
                  : 'Use my authenticator app'
                : 'Use a recovery code'}
            </button>
            <button
              type="button"
              onClick={() => {
                setMfaToken(null);
                setCode('');
                setError(null);
                // S-2: drop the "code sent to …" line too, so a second
                // attempt does not open claiming a code is already waiting.
                setSmsSentTo(null);
              }}
              disabled={submitting}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--text-muted)',
                fontSize: 12,
              }}
            >
              Back to sign in
            </button>
          </div>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Transaction Rocket" subtitle="Sign in to continue.">
      <form onSubmit={onSubmit} noValidate>
        <FormError message={error} />

        <div style={{ marginBottom: 16 }}>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <TextInput
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={submitting}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <TextInput
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            disabled={submitting}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            marginBottom: 24,
            flexWrap: 'wrap',
          }}
        >
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: 'var(--text-body)',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.currentTarget.checked)}
              disabled={submitting}
              style={{ cursor: 'pointer' }}
            />
            Remember me
          </label>
          <Link
            to="/forgot-password"
            style={{
              textDecoration: 'none',
              color: 'var(--brand-primary)',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" disabled={submitting || !email || !password} style={{ width: '100%' }}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthCard>
  );
}

/**
 * Map the raw error from signIn() to a user-friendly message.
 *
 * Why this exists: signIn() can fail three ways that all surface as Error
 * objects but need different messaging:
 *   - 401: bad credentials. The API returns "Invalid email or password" but
 *     we replace it with a slightly nudgier version that points at the
 *     forgot-password link, since that's the next action they'll likely want.
 *   - 429: rate limit. The API returns its own message; pass it through.
 *   - status 0 (network failure): apiFetch normalizes fetch's "Failed to fetch"
 *     into a clear "Couldn't reach the server" — just pass it through.
 *   - Anything else (5xx, locked account, etc.): pass the API's message through.
 */
function humanizeSignInError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return "Invalid email or password. If you've forgotten it, use “Forgot password?” below to reset.";
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Sign-in failed. Try again.';
}
